import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Col, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import authService from "../../../../services/authService";
import { useUser } from "../../../../context/UserContext";
import CustomFunction from "../custom_function/custom_function";
import "react-toastify/dist/ReactToastify.css";

const NewClientConfig = ({ onSave, initialData }) => {
  const { user, token } = useUser();
  const customFunctionRef = useRef();
  const [selectedOptions, setSelectedOptions] = useState({
    fullName: true,
    email: false,
    contactNumber: true,
    reasonForCalling: true,
    callingOnBehalf: false,
    whereDidYouHear: true,
    businessName: false,
  });

  const [messageEnabled, setMessageEnabled] = useState(true);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [transferEnabled, setTransferEnabled] = useState(false);
  const [functionEnabled, setFunctionEnabled] = useState(false);
  const [appointmentType, setAppointmentType] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [calcomApiKey, setCalcomApiKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [instructions, setInstructions] = useState(
    `- Maintain a friendly, empathetic & gentle tone and use simple, conversational language.
- Do not infer names. Repeat user-provided names exactly as transcribed. If unsure, ask for clarification.
- Keep questions concise and to the point; avoid unnecessary elaboration.
- Add natural fillers like 'uhm', 'hmm', or 'ahan' sparingly for a more human-like experience.
- Avoid interrupting the user and always wait for them to finish speaking.
- Speak at a medium pace and avoid rushing.`
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setSelectedOptions({
        fullName: initialData.fullName,
        email: initialData.emailAddress,
        contactNumber: initialData.contactNumber,
        reasonForCalling: initialData.reasonForCalling,
        callingOnBehalf: initialData.callingOnBehalfOf,
        whereDidYouHear: initialData.whereDidYouHearAboutUs,
        businessName: initialData.businessName,
      });
      setMessageEnabled(initialData.takeMessage);
      setScheduleEnabled(!initialData.takeMessage);
      setAppointmentType(initialData.isCalcomSelected ? "Cal.co" : "");
      setAdditionalInfo(initialData.calcomEventTypeId);
      setCalcomApiKey(initialData.calcomApiKey);
      setMessageText(initialData.message);
      setInstructions(initialData.instructions);
    }

    // Preset toggles based on user object
    if (user) {
      if (user.custom_function && !user.schedule_functions) {
        setFunctionEnabled(true);
        setMessageEnabled(false);
        setScheduleEnabled(false);
      } else if (!user.custom_function && user.schedule_functions) {
        setFunctionEnabled(false);
        setScheduleEnabled(true);
      }
    }
  }, [initialData, user]);

  const handleCheckboxChange = (e) => {
    setSelectedOptions({
      ...selectedOptions,
      [e.target.name]: e.target.checked,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update dynamic prompt in CustomFunction
      if (functionEnabled && customFunctionRef.current) {
        const success = await customFunctionRef.current.updateDynamicPrompt();
        if (!success) {
          throw new Error("Failed to update dynamic prompt");
        }
      }
      const newClientConfig = {
        fullName: selectedOptions.fullName,
        reasonForCalling: selectedOptions.reasonForCalling,
        emailAddress: selectedOptions.email,
        callingOnBehalfOf: selectedOptions.callingOnBehalf,
        contactNumber: selectedOptions.contactNumber,
        whereDidYouHearAboutUs: selectedOptions.whereDidYouHear,
        businessName: selectedOptions.businessName,
        instructions: instructions,
        takeMessage: messageEnabled,
        message: messageText,
        isCalcomSelected: appointmentType === "Cal.co",
        calcomApiKey: appointmentType === "Cal.co" ? calcomApiKey : "",
        calcomEventTypeId: appointmentType === "Cal.co" ? additionalInfo : "",
      };

      // Save client configuration
      await authService.updateInstructions(newClientConfig, token);

      // Update user function status
      await authService.updateUserFunction(
        user.email,
        functionEnabled,
        scheduleEnabled,
        token
      );

      onSave(newClientConfig);
    } catch (error) {
      console.error("Failed to save client configuration:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleToggle = () => {
    setScheduleEnabled(!scheduleEnabled);
    if (!scheduleEnabled) {
      setMessageEnabled(false);
      setFunctionEnabled(false);
    }
  };

  const handleMessageToggle = () => {
    setMessageEnabled(!messageEnabled);
    if (!messageEnabled) {
      setScheduleEnabled(false);
      setFunctionEnabled(false);
    }
  };

  const handleFunctionToggle = () => {
    setFunctionEnabled(!functionEnabled);
    if (!functionEnabled) {
      setMessageEnabled(false);
      setScheduleEnabled(false);
    }
  };

  const navigate = useNavigate();
  return (
    <div className="new-client-config-container">
      <div className="form-content flex-grow-1 ms-4">
        <Form>
          <Form.Group className="p-4 pt-0 border-0 card">
            <Form.Label>
              <h6 className="fs-three">2. Caller information collection</h6>
              <p className="gray-color">
                What information should we collect from a new client
              </p>
            </Form.Label>
            <div className="d-flex flex-wrap gap-3">
              <div className="d-flex flex-column flex-grow-1">
                {[
                  { label: "Full Name", name: "fullName" },
                  { label: "Email Address", name: "email" },
                  { label: "Contact Number", name: "contactNumber" },
                ].map((option) => (
                  <Form.Check
                    key={option.name}
                    type="checkbox"
                    label={option.label}
                    name={option.name}
                    checked={selectedOptions[option.name]}
                    onChange={handleCheckboxChange}
                    className="purple-custom-checkbox"
                  />
                ))}
              </div>
              <div className="d-flex flex-column flex-grow-1">
                {[
                  { label: "Reason for Calling", name: "reasonForCalling" },
                  { label: "Calling on Behalf of", name: "callingOnBehalf" },
                  {
                    label: "Where did you hear about us",
                    name: "whereDidYouHear",
                  },
                  { label: "Business Name", name: "businessName" },
                ].map((option) => (
                  <Form.Check
                    key={option.name}
                    type="checkbox"
                    label={option.label}
                    name={option.name}
                    checked={selectedOptions[option.name]}
                    onChange={handleCheckboxChange}
                    className="purple-custom-checkbox"
                  />
                ))}
              </div>
            </div>
          </Form.Group>

          <Form.Group className="p-4 mb-3 border-0 card">
            <Form.Label>
              <h6 className="fs-three">3. Call handling Options</h6>
              <p className="gray-color">What should we do next?</p>
            </Form.Label>
            <div className="d-flex flex-column gap-2">
              <Row className="align-items-center border border-light-subtle rounded-3  px-3 py-3">
                <Col xs="auto">
                  <img
                    src="/assets/message-square.svg"
                    alt="Info"
                    className="p-2 bg-icon"
                  />
                </Col>
                <Col className="px-0">
                  <label className="fw-bold">Take a Message</label>
                </Col>
                <Col xs="auto fs-5">
                  <Form.Check
                    type="switch"
                    id="message-switch"
                    checked={messageEnabled}
                    onChange={handleMessageToggle}
                    className={messageEnabled ? "toggle-btn" : ""}
                  />
                </Col>
              </Row>

              <Row className="align-items-center border border-light-subtle rounded-3  px-3 py-3">
                <Col xs="auto">
                  <img
                    src="/assets/calendar.svg"
                    alt="Info"
                    className="p-2 bg-icon"
                  />
                </Col>
                <Col className="px-0">
                  <label className="fw-bold">Schedule Appointment</label>
                </Col>
                <Col xs="auto fs-5">
                  <Form.Check
                    type="switch"
                    id="schedule-switch"
                    checked={scheduleEnabled}
                    onChange={handleScheduleToggle}
                    className={scheduleEnabled ? "toggle-btn" : ""}
                  />
                </Col>
                {scheduleEnabled && (
                  <Row>
                    <Form.Group className="mt-2 col-md-6 position-relative">
                      <Form.Label>Select Option</Form.Label>
                      <Form.Control
                        as="select"
                        value={appointmentType}
                        onChange={(e) => setAppointmentType(e.target.value)}
                      >
                        <option value="">Select an option</option>
                        <option value="Cal.co">Cal.com</option>
                      </Form.Control>
                      <img
                        src="/assets/chevron-down.svg"
                        alt="Icon"
                        className="position-absolute dropdown-icon"
                      />
                    </Form.Group>
                    {appointmentType === "Cal.co" && (
                      <>
                        <Form.Group className="mt-2 col-md-6">
                          <Form.Label>Event Id</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter here"
                            value={additionalInfo}
                            onChange={(e) => setAdditionalInfo(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="mt-2 col-md-12 mt-3">
                          <Form.Label>Cal.com API Key</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter API Key"
                            value={calcomApiKey}
                            onChange={(e) => setCalcomApiKey(e.target.value)}
                          />
                        </Form.Group>
                        <p className="gray-color mt-3 mb-0">
                          If you don't have a Cal.com account, you can create
                          one{" "}
                          <Link
                            className="purple-color fw-bold text-decoration-none"
                            to="https://cal.com/"
                            target="_blank"
                          >
                            here
                          </Link>
                          . Please make sure to provide the correct details. If
                          details are incorrect then this will result in no
                          appointments scheduled.
                        </p>
                      </>
                    )}
                  </Row>
                )}
              </Row>
              <Row className="align-items-center border border-light-subtle rounded-3  px-3 py-3">
                <Col xs="auto">
                  <img
                    src="/assets/carbon_settings-edit.svg"
                    alt="Info"
                    className="p-2 bg-icon"
                  />
                </Col>
                <Col className="px-0">
                  <label className="fw-bold">Custom Function</label>
                </Col>
                <Col xs="auto fs-5">
                  <Form.Check
                    type="switch"
                    id="function-switch"
                    checked={functionEnabled}
                    onChange={handleFunctionToggle}
                    className={functionEnabled ? "toggle-btn" : ""}
                  />
                </Col>

                {functionEnabled && (
                  <div className="add-btn-container">
                    <Row className="py-3">
                      <p className="gray-color cf-info">
                        Custom functions enhance Nouraa by integrating APIs,
                        adding knowledge, or enabling custom logic.
                      </p>
                      <hr className="border-dark-subtle" />
                    </Row>
                  </div>
                )}
              </Row>

              <Row className="disable-btn align-items-center border border-light-subtle rounded-3 px-3 py-3">
                <Col xs="auto">
                  <img
                    src="/assets/phone-forwarded.svg"
                    alt="Info"
                    className="p-2 bg-icon"
                  />
                </Col>
                <Col className="px-0">
                  <label className="fw-bold gray-color">Transfer Call</label>
                </Col>
                <Col xs="auto fs-5">
                  <Form.Check
                    type="switch"
                    id="transfer-switch"
                    checked={transferEnabled}
                    onChange={() => setTransferEnabled(!transferEnabled)}
                    className={transferEnabled ? "toggle-btn" : ""}
                    disabled
                  />
                </Col>
              </Row>
              <div className="booking-inst col-lg-12 my-3">
                <Form.Label>
                  <h6 className="fs-three">4. Agent Instruction (Optional)</h6>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  placeholder="Enter the instruction..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>
            </div>
          </Form.Group>
        </Form>
      </div>

      <div className="d-flex justify-content-between align-items-center px-4 ps-5 mt-4">
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="expect-call-checkbox"
            className="fw-bold purple-custom-checkbox"
            defaultChecked
            readOnly
          />
          <Link
            to=""
            className="fs-one text-decoration-none black-color fw-bold ms-2"
          >
            I only expect this type of call
          </Link>
        </div>
        <div className="d-flex align-items-center">
          <span className="me-3 fs-one gray-color">
            Changes will go live immediately
          </span>
          <Button
            className="fw-bold"
            variant="none"
            onClick={() => navigate("/agent_settings")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="ms-2 bg-purple-color fw-bold greeting-save-btn"
            disabled={saving}
          >
            {saving ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      {/* CustomFunction Modal */}
      <div className="position-relative">
        {functionEnabled && (
          <CustomFunction
            ref={customFunctionRef}
            handleCloseModal={() => setFunctionEnabled(false)}
            instructions={instructions}
          />
        )}
      </div>
    </div>
  );
};

export default NewClientConfig;
